import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { Alert, Badge, Button, Card, statusTone } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { useSession } from '../../lib/session';
import type { Org } from '../../lib/types';

export function OrgDetailPage() {
  const { t } = useTranslation();
  const { orgId } = useParams();
  const { identity, refresh, switchOrg } = useSession();
  const navigate = useNavigate();
  const [org, setOrg] = useState<Org | null>(null);
  const [audit, setAudit] = useState<
    { id: string; at: string; action: string; actorEmail?: string; meta?: unknown }[]
  >([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const canProvision = !!identity?.user.isVaultSuperuser;

  const load = useCallback(async () => {
    setOrg(await api<Org>(`/api/v1/superadmin/orgs/${orgId}`));
    setAudit((await api<{ entries: typeof audit }>(`/api/v1/superadmin/audit?orgId=${orgId}`)).entries);
  }, [orgId]);
  useEffect(() => {
    void load();
  }, [load]);

  const act = async (name: string, path: string, body?: unknown) => {
    setBusy(name);
    setErr(null);
    try {
      const r = await api<Org>(`/api/v1/superadmin/orgs/${orgId}/${path}`, {
        method: 'POST',
        json: body ?? {},
      });
      setOrg(r);
      await load();
      // the SuperAdmin may have just become a member of this org (pilot: nurie.ai) -> re-resolve identity for the nav
      if (['provision', 'suspend', 'admin', 'reset'].includes(name)) await refresh();
    } catch (e) {
      setErr(
        e instanceof ApiError
          ? t([`sa.err_${e.code}`, 'sa.err_generic'], { code: e.code }) + ` ${e.message}`
          : String(e),
      );
    } finally {
      setBusy(null);
    }
  };
  if (!org) return <p className="text-slate-500">{t('app.loading')}</p>;
  const steps: Org['provisioning']['steps'] = org.provisioning?.steps ?? [];
  const ids = ['P1', 'P2', 'P3', 'P4', 'P5', 'P6'] as const;
  const p3 = steps.find((x) => x.id === 'P3');
  const linkedElsewhere =
    p3?.status === 'failed' && p3.error === 'team_already_linked'
      ? String((p3.result as { otherOrgName?: string } | undefined)?.otherOrgName ?? '?')
      : null;
  const changeAdmin = () => {
    const email = window.prompt(t('sa.changeAdminPrompt'), org.adminEmail)?.trim();
    if (email && email.toLowerCase() !== org.adminEmail) void act('admin', 'admin', { email });
  };
  const resetTeam = () => {
    const v = window.prompt(t('sa.resetTeamConfirm'), '');
    if (v === null) return;
    void act('reset', 'reset-team', v.trim() ? { adminEmail: v.trim() } : {});
  };
  const openOrg = async () => {
    await switchOrg(org.id === identity?.homeOrgId ? null : org.id);
    navigate('/');
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Link to="/superadmin" className="text-sm text-emerald-700 underline">
          ← {t('sa.back')}
        </Link>
        <h1 className="text-2xl font-semibold">{org.name}</h1>
        <Badge tone={statusTone(org.status)}>{org.status}</Badge>
      </div>
      {err && <Alert>{err}</Alert>}
      {linkedElsewhere && <Alert>{t('sa.teamAlreadyLinked', { name: linkedElsewhere })}</Alert>}
      <div className="grid gap-4 md:grid-cols-2">
        <Card title={t('sa.orgs')}>
          <dl className="grid grid-cols-[10rem_1fr] gap-y-1 text-sm">
            <dt className="text-slate-500">{t('sa.slug')}</dt>
            <dd className="font-mono">{org.slug}</dd>
            <dt className="text-slate-500">{t('sa.adminEmail')}</dt>
            <dd>{org.adminEmail}</dd>
            <dt className="text-slate-500">{t('sa.contractRef')}</dt>
            <dd>
              {org.contract.ref} {org.contract.endsAt && `(→ ${org.contract.endsAt})`}
            </dd>
            <dt className="text-slate-500">{t('sa.team')}</dt>
            <dd className="font-mono text-xs">{org.vaultsageTeamId ?? '-'}</dd>
            <dt className="text-slate-500">{t('sa.subscription')}</dt>
            <dd>
              {org.subscription
                ? `${org.subscription.platform} · tier ${org.subscription.tier} · ${org.subscription.maxSeats} seats`
                : '-'}
            </dd>
            <dt className="text-slate-500">{t('sa.expires')}</dt>
            <dd>{org.subscription?.expiresAt?.slice(0, 10) ?? '-'}</dd>
            <dt className="text-slate-500">{t('sa.members')}</dt>
            <dd>
              {org.memberCount ?? 0} (+{org.inviteCount ?? 0} invites)
            </dd>
          </dl>
          <div className="mt-4 flex flex-wrap gap-2">
            {org.status !== 'offboarded' && (
              <Button variant="secondary" disabled={busy !== null} onClick={() => void openOrg()}>
                {t('sa.open')}
              </Button>
            )}
            <Button variant="secondary" disabled={busy !== null} onClick={changeAdmin}>
              {t('sa.changeAdmin')}
            </Button>
            {(org.vaultsageTeamId || org.status === 'provisioning_failed') && (
              <Button variant="danger" disabled={busy !== null} onClick={resetTeam}>
                {t('sa.resetTeam')}
              </Button>
            )}
            {org.status !== 'active' && org.status !== 'suspended' && (
              <Button
                disabled={!canProvision || busy !== null}
                title={canProvision ? '' : t('sa.needsVaultSuperuser')}
                onClick={() => void act('provision', 'provision')}
              >
                {busy === 'provision'
                  ? t('sa.provisioning')
                  : steps.length
                    ? t('sa.retry')
                    : t('sa.provision')}
              </Button>
            )}
            {org.subscription?.platform === 'sandbox' && (
              <Button
                variant="secondary"
                disabled={!canProvision || busy !== null}
                onClick={() => void act('extend', 'extend', { days: 365 })}
              >
                {t('sa.extend')}
              </Button>
            )}
            {org.status === 'active' && (
              <Button
                variant="danger"
                disabled={busy !== null}
                onClick={() => void act('suspend', 'suspend', { suspended: true })}
              >
                {t('sa.suspend')}
              </Button>
            )}
            {org.status === 'suspended' && (
              <Button
                variant="secondary"
                disabled={busy !== null}
                onClick={() => void act('suspend', 'suspend', { suspended: false })}
              >
                {t('sa.reactivate')}
              </Button>
            )}
          </div>
          {!canProvision && <p className="mt-2 text-xs text-amber-700">{t('sa.needsVaultSuperuser')}</p>}
        </Card>
        <Card title={t('sa.steps')}>
          <ol className="space-y-1 text-sm">
            {ids.map((id) => {
              const s = steps.find((x) => x.id === id);
              const tone =
                s?.status === 'ok'
                  ? 'green'
                  : s?.status === 'skipped'
                    ? 'slate'
                    : s?.status === 'failed'
                      ? 'red'
                      : 'slate';
              return (
                <li key={id} className="flex items-start gap-2">
                  <Badge tone={tone}>{s?.status ?? '·'}</Badge>
                  <span>
                    <span className="font-mono text-xs text-slate-500">{id}</span> {t(`sa.step_${id}`)}
                    {s?.result && (
                      <span className="ml-1 text-xs text-slate-500">{JSON.stringify(s.result)}</span>
                    )}
                    {s?.error && <span className="ml-1 text-xs text-red-600">{s.error}</span>}
                  </span>
                </li>
              );
            })}
          </ol>
          {org.provisioning?.failedReason && (
            <p className="mt-3 text-sm text-red-700">
              {t('sa.failedReason')}: {org.provisioning.failedReason}
            </p>
          )}
        </Card>
      </div>
      <Card title={t('sa.audit')}>
        <ul className="divide-y divide-slate-100 text-sm">
          {audit.map((a) => (
            <li key={a.id} className="flex gap-3 py-1">
              <span className="w-40 shrink-0 font-mono text-xs text-slate-500">
                {a.at.replace('T', ' ').slice(0, 19)}
              </span>
              <span className="w-44 shrink-0 font-medium">{a.action}</span>
              <span className="text-slate-600">{a.actorEmail}</span>
              <span className="truncate text-xs text-slate-500">{a.meta ? JSON.stringify(a.meta) : ''}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
